import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  Button,
  IconButton,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  convertDeviceType,
  displayCreatedAt,
  displayDobTime,
} from "utils/helpers";
import * as SC from "./RecipientDetails.style";
import StatusChanger, { CanceledStatusSelect } from "./StatusChanger";
import { ReactComponent as LinkIcon } from "images/link-icon.svg";

export const ReferralDetailsTab = ({
  id,
  admins,
  marketers,
  schedulers,
  providers,
  hardButton,
  patient,
  getData,
  createdAt,
  status,
  note,
  buttonId,
  buttonType,
  practiceName,
  practiceSpeciality,
  practiceAddress,
  practicePhone,
  practiceMarketingRegion,
  originatorName,
  originatorPhone,
  originatorEmail,
  originatorJobTitle,
  recipientName,
  recipientPhone,
  recipientAddress,
  type,
  question,
  options,
  isResendButtonVisible,
  handleStatusChange,
  handleResendFormLink,
  isSuperAdmin,
  locations,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Stack mt={2} gap={2}>
      <SC.Paper>
        <Stack alignItems={"stretch"} width={"100%"} gap={1}>
          <SC.InfoSection>
            <Stack direction={"row"} alignItems={"center"} gap={1}>
              <IconButton onClick={getData}>
                <RefreshIcon />
              </IconButton>
              <SC.Label>Status:</SC.Label>
              {isSuperAdmin ? (
                <StatusChanger
                  initialValue={status}
                  onChange={handleStatusChange}
                  options={options}
                />
              ) : (
                <>
                  {status === "Canceled" ? (
                    <CanceledStatusSelect>{status}</CanceledStatusSelect>
                  ) : (
                    <StatusChanger
                      initialValue={status}
                      onChange={handleStatusChange}
                      options={options}
                    />
                  )}
                </>
              )}
            </Stack>
            <SC.Info>
              <SC.Label>Created at: </SC.Label>
              {displayCreatedAt({ createdAt })}
            </SC.Info>
            <SC.Info>
              <SC.Label>Referral ID: </SC.Label>
              {id.slice(0, 8)}...
            </SC.Info>
            {!buttonId ? (
              <SC.Info>
                <SC.Label>Source: </SC.Label> <LinkIcon />
                Embedded form or link
              </SC.Info>
            ) : (
              <SC.Info>
                <SC.Label>Button ID: </SC.Label>
                {buttonType === "Virtual" ? buttonId.slice(0, 8) : buttonId}
              </SC.Info>
            )}
            {!!hardButton?.name && (
              <SC.Info>
                <SC.Label>Button Name: </SC.Label>
                {hardButton?.name}
              </SC.Info>
            )}
            {!!buttonType && (
              <SC.Info>
                <SC.Label>Button Type:</SC.Label>{" "}
                {convertDeviceType(buttonType)}
              </SC.Info>
            )}
          </SC.InfoSection>
        </Stack>
      </SC.Paper>
      {(!!note || type === "Question") && (
        <SC.Paper>
          <Stack
            direction={isMobile ? "column" : "row"}
            justifyContent={"space-between"}
            gap={2}
            width={"100%"}
          >
            {type === "Question" && (
              <SC.InfoSectionHighlighted flex={1}>
                <SC.Info>
                  <SC.Label>Question:</SC.Label>{" "}
                  {question?.trim()?.length > 0 ? question : "not entered"}
                </SC.Info>
              </SC.InfoSectionHighlighted>
            )}
            {!!note && (
              <SC.InfoSection flex={1}>
                <SC.Info>
                  <Stack direction={"row"} alignItems={"center"} gap={2}>
                    <SC.Label>Note:</SC.Label>
                    <Box sx={{ fontSize: "0.9rem" }}>{note}</Box>
                  </Stack>
                </SC.Info>
              </SC.InfoSection>
            )}
          </Stack>
        </SC.Paper>
      )}
      {isResendButtonVisible && (
        <SC.Info>
          <SC.Label
            sx={{
              fontSize: "15px",
              marginLeft: 1,
              lineHeight: "18px",
            }}
          >
            {type === "Notification" &&
              "The Referral Form has not been completed yet. Kindly request the sender to fill it out using the provided link."}
            {type === "Question" &&
              "To proceed with creating the referral, please ask the sender to complete the form using the provided link."}
            {type === "Referral" &&
              "The patient details have not been entered yet. Please ask the sender to complete the information using the provided link."}
          </SC.Label>
          <Button
            onClick={handleResendFormLink}
            variant="outlined"
            sx={{ padding: "5px 10px" }}
          >
            Resend Referral Form Link
          </Button>
        </SC.Info>
      )}
      <SC.Paper>
        <Stack direction={"row"} gap={4}>
          <Stack
            direction={isMobile ? "column" : "row"}
            rowGap={1}
            columnGap={4}
          >
            <Stack width={"100%"} gap={1}>
              <SC.Title>Practice</SC.Title>
              <SC.InfoSection>
                <SC.Info>
                  <SC.Label>Name:</SC.Label> {practiceName}
                </SC.Info>
                <SC.Info>
                  <SC.Label>Speciality:</SC.Label> {practiceSpeciality}
                </SC.Info>
                {!!practicePhone && (
                  <SC.Info>
                    <SC.Label>Phone:</SC.Label>{" "}
                    <a href={`tel:${practicePhone}`}>{practicePhone}</a>
                  </SC.Info>
                )}
                <SC.Info>
                  <SC.Label>Address:</SC.Label> {practiceAddress}
                </SC.Info>
                {/* <SC.Info>
            <SC.Label>Email:</SC.Label>{" "}
            <a href={`mailto:${practiceEmail}`}>{practiceEmail}</a>
          </SC.Info> */}
                {!!practiceMarketingRegion && (
                  <SC.Info>
                    <SC.Label>Marketing region:</SC.Label>{" "}
                    {practiceMarketingRegion}
                  </SC.Info>
                )}
              </SC.InfoSection>
            </Stack>
            {!!providers?.length && (
              <Stack width={"100%"} maxWidth={"300px"} gap={1}>
                <SC.Title>Provider{providers?.length > 1 ? "s" : ""}</SC.Title>
                <SC.InfoSection>
                  <Stack
                    direction={"row"}
                    justifyContent={"space-between"}
                    alignContent={"flex-start"}
                    flexWrap={"wrap"}
                    key={id}
                    gap={4}
                  >
                    {providers?.map(({ id, name, speciality }) => (
                      <Stack
                        key={id}
                        flex={1}
                        direction={"row"}
                        flexWrap={"wrap"}
                        gap={"10px"}
                      >
                        <SC.Info>
                          <SC.Label>Name:</SC.Label> {name}
                        </SC.Info>
                        <SC.Info>
                          <SC.Label>Speciality:</SC.Label> {speciality}
                        </SC.Info>
                      </Stack>
                    ))}
                  </Stack>
                </SC.InfoSection>
              </Stack>
            )}
            <Stack width={"100%"} maxWidth={"360px"} gap={1}>
              <SC.Title>Sender</SC.Title>
              <SC.InfoSection>
                <SC.Info>
                  <SC.Label>Name:</SC.Label> {originatorName}
                </SC.Info>
                {!!originatorJobTitle && (
                  <SC.Info>
                    <SC.Label>Job/Title:</SC.Label> {originatorJobTitle}
                  </SC.Info>
                )}
                <SC.Info>
                  <SC.Label>Phone:</SC.Label>{" "}
                  <a href={`tel:${originatorPhone}`}>{originatorPhone}</a>
                </SC.Info>
                <SC.Info>
                  <SC.Label>Email:</SC.Label>{" "}
                  <a href={`mailto:${originatorEmail}`}>{originatorEmail}</a>
                </SC.Info>
              </SC.InfoSection>
            </Stack>
          </Stack>
        </Stack>
      </SC.Paper>
      {!!patient && (
        <SC.Paper>
          <Stack direction={isMobile ? "column" : "row"} gap={4}>
            <Stack width={"100%"} gap={1}>
              <SC.Title>Patient</SC.Title>
              <SC.InfoSection>
                <SC.Info>
                  <SC.Label>Name:</SC.Label> {patient?.name}
                </SC.Info>
                <SC.Info>
                  <SC.Label>Date of Birth:</SC.Label>{" "}
                  {displayDobTime(patient?.dob)}
                </SC.Info>
                <SC.Info>
                  <SC.Label>Gender:</SC.Label> {patient?.gender}
                </SC.Info>
                <SC.Info>
                  <SC.Label>Phone:</SC.Label>{" "}
                  <a href={`tel:${patient?.phone}`}>{patient?.phone}</a>
                </SC.Info>
                <SC.Info>
                  <SC.Label>Location:</SC.Label> {patient?.location}
                </SC.Info>
                <SC.Info>
                  <SC.Label>Reason:</SC.Label> {patient?.reason}
                </SC.Info>
                <SC.Info>
                  <SC.Label>Urgency:</SC.Label> {patient?.urgency}
                </SC.Info>
              </SC.InfoSection>
            </Stack>
          </Stack>
        </SC.Paper>
      )}
      <SC.Paper>
        <Stack direction={isMobile ? "column" : "row"} gap={4}>
          <SC.Organizations>
            <Stack width={"100%"} gap={1}>
              <Box>
                <SC.Title>Recipient</SC.Title>
                <SC.InfoSection>
                  <SC.Info>
                    <SC.Label>Name:</SC.Label> {recipientName}
                  </SC.Info>
                  <SC.Info>
                    <SC.Label>Phone:</SC.Label>{" "}
                    <a href={`tel:${recipientPhone}`}>{recipientPhone}</a>
                  </SC.Info>
                  <SC.Info>
                    <SC.Label>Address:</SC.Label> {recipientAddress}
                  </SC.Info>
                </SC.InfoSection>
              </Box>
              {locations?.length > 0 &&
                locations.map(
                  ({
                    name,
                    street,
                    street2,
                    city,
                    state,
                    zipCode,
                    phone,
                    speciality,
                  }) => (
                    <Box>
                      <SC.Title>Location</SC.Title>
                      <SC.InfoSection>
                        <SC.Info>
                          <SC.Label>Name:</SC.Label> {name}
                        </SC.Info>
                        {!!speciality && (
                          <SC.Info>
                            <SC.Label>Speciality:</SC.Label> {speciality}
                          </SC.Info>
                        )}
                        {!!phone && (
                          <SC.Info>
                            <SC.Label>Phone:</SC.Label> {phone}
                          </SC.Info>
                        )}
                        <SC.Info>
                          <SC.Label>Address:</SC.Label>{" "}
                          {[street, street2, city, state, zipCode]
                            .filter(Boolean)
                            .join(", ")}
                        </SC.Info>
                      </SC.InfoSection>
                    </Box>
                  )
                )}
            </Stack>
          </SC.Organizations>
          <SC.RecipientUsers>
            {admins?.length > 0 && (
              <Stack flex={1} gap={1}>
                <SC.Title>Recipient Admins</SC.Title>
                <SC.InfoSection>
                  {admins.map(({ name, email, phone }) => (
                    <SC.ListItem key={`adm-${email}`}>
                      {name},{" "}
                      <a href={`mailto:${email}`} className="whitespace-nowrap">
                        {email}
                      </a>
                      {phone ? (
                        <>
                          ,{" "}
                          <a
                            href={`tel:${phone}`}
                            className="whitespace-nowrap"
                          >
                            {phone}
                          </a>
                        </>
                      ) : (
                        ""
                      )}
                    </SC.ListItem>
                  ))}
                </SC.InfoSection>
              </Stack>
            )}
            {marketers?.length > 0 && (
              <Stack flex={1} gap={1}>
                <SC.Title>Marketers</SC.Title>
                <SC.InfoSection>
                  {marketers.map(
                    ({ name, email, phone, marketingRegionId }) => (
                      <SC.ListItem key={`mark-${email}`}>
                        {name},{" "}
                        <a
                          href={`mailto:${email}`}
                          className="whitespace-nowrap"
                        >
                          {email}
                        </a>
                        ,{" "}
                        <a href={`tel:${phone}`} className="whitespace-nowrap">
                          {phone}
                        </a>
                        {marketingRegionId ? `, ${marketingRegionId}` : ""}
                      </SC.ListItem>
                    )
                  )}
                </SC.InfoSection>
              </Stack>
            )}
            {schedulers?.length > 0 && (
              <Stack flex={1} gap={1}>
                <SC.Title>Schedulers</SC.Title>
                <SC.InfoSection>
                  {schedulers.map(({ name, email, phone }) => (
                    <SC.ListItem key={`sched-${email}`}>
                      {name},{" "}
                      <a href={`mailto:${email}`} className="whitespace-nowrap">
                        {email}
                      </a>
                      ,{" "}
                      <a href={`tel:${phone}`} className="whitespace-nowrap">
                        {phone}
                      </a>
                    </SC.ListItem>
                  ))}
                </SC.InfoSection>
              </Stack>
            )}
          </SC.RecipientUsers>
        </Stack>
      </SC.Paper>
    </Stack>
  );
};
